import { useMutation, useQueryClient } from "@tanstack/react-query";

import { requireData } from "../../shared/api/api-error.js";
import { useApi } from "../../shared/api/api-context.js";
import type { TradingEngineClient } from "../../shared/api/client.js";
import { lifecycleActions, type BotView } from "./models.js";
import { botKeys, settleOperation } from "./queries.js";

export const filteredLiveConcurrency = 3;

export type FilteredLiveAction = "ready" | "start" | "stop";

export interface FilteredLiveOutcome {
  readonly botId: string;
  readonly botName: string;
  readonly status: "failed" | "succeeded";
}

export function eligibleFilteredLiveBots(
  bots: readonly BotView[],
  action: FilteredLiveAction,
): readonly BotView[] {
  return bots.filter(
    (bot) =>
      lifecycleActions(bot.state).includes(action) &&
      (action !== "start" || bot.liveBrokerAccountId !== undefined),
  );
}

async function applyOne(
  api: TradingEngineClient,
  bot: BotView,
  action: FilteredLiveAction,
): Promise<void> {
  const accepted = requireData(
    await api.POST("/bots/{botId}/actions/{action}", {
      body: {
        expectedVersion: bot.version,
        ...(action === "start" && bot.liveBrokerAccountId !== undefined
          ? { brokerAccountId: bot.liveBrokerAccountId }
          : {}),
      },
      params: {
        header: { "x-idempotency-key": crypto.randomUUID() },
        path: { action, botId: bot.id },
      },
    }),
  );
  await settleOperation(api, accepted.operation);
}

export async function applyFilteredLiveAction(
  api: TradingEngineClient,
  bots: readonly BotView[],
  action: FilteredLiveAction,
  concurrency = filteredLiveConcurrency,
): Promise<readonly FilteredLiveOutcome[]> {
  const outcomes = new Array<FilteredLiveOutcome>(bots.length);
  let nextIndex = 0;
  const worker = async () => {
    while (nextIndex < bots.length) {
      const index = nextIndex;
      nextIndex += 1;
      const bot = bots[index];
      if (bot === undefined) continue;
      try {
        await applyOne(api, bot, action);
        outcomes[index] = { botId: bot.id, botName: bot.name, status: "succeeded" };
      } catch {
        outcomes[index] = { botId: bot.id, botName: bot.name, status: "failed" };
      }
    }
  };
  const workerCount = Math.min(Math.max(1, concurrency), bots.length);
  await Promise.all(Array.from({ length: workerCount }, worker));
  return outcomes;
}

export function useApplyFilteredLiveAction() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      action,
      bots,
    }: {
      action: FilteredLiveAction;
      bots: readonly BotView[];
    }) => applyFilteredLiveAction(api, bots, action),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: botKeys.all }),
  });
}
